import MomentUtils from "@date-io/moment";
import { Tooltip } from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import {
    KeyboardDateTimePicker,
    MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import { useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { BsFiletypeHtml } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import { toastError } from "../../../utils";
import HtmlImportGuide from "../../../components/Common/HtmlImportGuide";
import { DownloadPdf } from "./downloadPdf";

const StatementofFactsModel = ({ state, setState, onDownloadElectricPdf }) => {
    const vesselFields = [
        { key: "vesselName", label: "Vessel Name" },
        { key: "grade", label: "Cargo Grade" },
        { key: "billOfLadingDate", label: "B/L Date" },
        { key: "voyageNo", label: "Voyage No:" },
        { key: "charterer", label: "Charterer" },
        { key: "port", label: "Laytime for the Port" },
        { key: "operation", label: "Operation Type" },
        { key: "cargoQty", label: "Cargo Quantity (MT)" },
        { key: "shipmentId", label: "Shipment ID" },
        { key: "timeZone", label: "Time Zone" },
    ];
    const inputFileRef = useRef(null);
    const [openUploadHTML, setOpenUploadHTML] = useState(false);
    const onButtonClick = () => {
        setOpenUploadHTML(true);
    };

    function getInputValue(doc, id) {
        const el = doc.getElementById(id);
        return el ? el.value : "";
    }

    const handleFile = async (e) => {
        const file = e.target.files[0];
        e.target.value = null;
        if (!file) return;

        try {
            const htmlText = await file.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlText, "text/html");

            const vesselData = {
                vesselName: getInputValue(doc, "vesselName") || "",
                grade: getInputValue(doc, "cargoGrade") || "",
                billOfLadingDate: getInputValue(doc, "billOfLadingDate") || "",
                voyageNo: getInputValue(doc, "voyageNo") || "",
                charterer: getInputValue(doc, "charterer") || "",
                port: getInputValue(doc, "laytimeForPort") || "",
                operation: getInputValue(doc, "operationType") || "",
                cargoQty: getInputValue(doc, "cargoQuantity") || "",
                shipmentId: getInputValue(doc, "shipmentId") || "",
                timeZone: getInputValue(doc, "timeZone") || "",
            };

            const events = Array.from(doc.querySelectorAll("#events-table-body tr"))
                .map((tr) => {
                    const tds = tr.querySelectorAll("td");
                    if (tds.length < 3) return null;
                    return {
                        day: tds[0].querySelector("input")?.value || "",
                        time: tds[1].querySelector("input")?.value || "",
                        event: tds[2].querySelector("input")?.value || "",
                    };
                })
                .filter(Boolean);

            const userFields = Array.from(
                doc.querySelectorAll("#user-fields-container .user-field")
            ).map((div, index) => {
                const [nameInput, valueInput] = div.querySelectorAll("input");
                return {
                    label: nameInput?.value || `User Defined Fields ${index + 1}`,
                    value: valueInput ? (isNaN(valueInput.value) ? valueInput.value : Number(valueInput.value)) : "",
                };
            });

            const remarks = {
                general: Array.from(
                    doc.querySelectorAll("#remarks-container-general .remark-item textarea")
                ).map((ta) => ta.value || ""),
                shippers: Array.from(
                    doc.querySelectorAll("#remarks-container-shippers .remark-item textarea")
                ).map((ta) => ta.value || ""),
                shipOwners: Array.from(
                    doc.querySelectorAll("#remarks-container-shipOwners .remark-item textarea")
                ).map((ta) => ta.value || ""),
            };

            const sof_authors = Array.from(
                doc.querySelectorAll("#authors-container .author-card")
            ).map((card, index) => {
                const [nameInput, titleInput] = card.querySelectorAll("input");
                const remarkInput = card.querySelector("textarea");
                return {
                    id: index + 1,
                    name: nameInput?.value || "",
                    designation: titleInput?.value || "",
                    details: remarkInput?.value || "",
                };
            });

            const dataToSet = {
                events,
                userFields,
                remarks,
                sof_authors,
                vesselData,
            };

            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    ...dataToSet,
                },
            }));

        } catch (err) {
            toastError("Parsing error. Please upload a valid Statement of Facts HTML file.");
        }
    };

    const updateState = (path, value) => {
        setState((prev) => {
            const newState = { ...prev };
            const keys = path.split(".");
            let current = newState.data;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newState;
        });
    };

    const handleVesselChange = (field, value) =>
        updateState(`vesselData.${field}`, value);

    const handleEventChange = (index, field, value) => {
        const newEvents = [...(state?.events || [])];
        newEvents[index] = { ...newEvents[index], [field]: value };

        if (field === "time") {
            const date = new Date(value);
            newEvents[index].day = date.toLocaleString("en-US", { weekday: "short" });
        }

        updateState("events", newEvents);
    };

    const addRow = (index) => {
        const newEvents = [...(state?.events || [])];
        newEvents.splice(index + 1, 0, { day: "", time: null, event: "" });
        updateState("events", newEvents);
    };

    const removeRow = (index) => {
        if (state?.events?.length <= 1) return;
        const newEvents = [...(state?.events || [])];
        newEvents.splice(index, 1);
        updateState("events", newEvents);
    };

    const handleUserFieldChange = (index, field, value) => {
        const newFields = [...(state?.userFields || [])];
        newFields[index] = { ...newFields[index], [field]: value };
        updateState("userFields", newFields);
    };

    const addUserField = (index = (state?.userFields?.length || 1) - 1) => {
        const newFields = [...(state?.userFields || [])];
        newFields.splice(index + 1, 0, {
            label: `Field ${newFields.length + 1}`,
            value: 0,
        });
        updateState("userFields", newFields);
    };

    const removeUserField = (index) => {
        if (state?.userFields?.length <= 1) return;
        const newFields = [...(state?.userFields || [])];
        newFields.splice(index, 1);
        updateState("userFields", newFields);
    };

    const handleRemarkChange = (type, index, value) => {
        const newRemarks = { ...state?.remarks };
        newRemarks[type] = newRemarks[type]?.map((item, i) =>
            i === index ? value : item
        );
        updateState("remarks", newRemarks);
    };

    const addRemark = (type, index = -1) => {
        const newRemarks = { ...state?.remarks };
        newRemarks[type] =
            index === -1
                ? [...(newRemarks[type] || []), ""]
                : [
                    ...(newRemarks[type] || []).slice(0, index + 1),
                    "",
                    ...(newRemarks[type] || []).slice(index + 1),
                ];
        updateState("remarks", newRemarks);
    };

    const removeRemark = (type, index) => {
        if (state?.remarks?.[type]?.length <= 1) return;
        const newRemarks = { ...state?.remarks };
        newRemarks[type] = newRemarks[type].filter((_, i) => i !== index);
        updateState("remarks", newRemarks);
    };

    const handlePersonnelChange = (id, field, value) => {
        const newAuthors = state?.sof_authors?.map((person) =>
            person.id === id ? { ...person, [field]: value } : person
        );
        updateState("sof_authors", newAuthors);
    };

    const handleAddPerson = () => {
        const newId =
            state?.sof_authors?.length > 0
                ? Math.max(...state?.sof_authors?.map((p) => p.id)) + 1
                : 1;

        const newAuthor = {
            id: newId,
            name: "",
            designation: "",
            details: "",
        };

        updateState("sof_authors", [...(state?.sof_authors || []), newAuthor]);
    };

    const handleDeletePerson = (id) => {
        if (state?.sof_authors?.length <= 1) {
            toastError("You need to have at least one person");
            return;
        }
        updateState(
            "sof_authors",
            state?.sof_authors.filter((person) => person.id !== id)
        );
    };

    return (
        <div className="space-y-3 font-sans">
            <div className="flex items-center justify-end gap-1.5 mb-2">
                <input
                    ref={inputFileRef}
                    type="file"
                    accept=".html,.htm"
                    style={{ display: "none" }}
                    onChange={(event) => { setOpenUploadHTML(false); handleFile(event); }}
                />
                <button
                    onClick={onButtonClick}
                    className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors"
                >
                    <BsFiletypeHtml size={12} />
                    <span>Upload HTML</span>
                </button>
                <DownloadPdf
                    pdfData={state}
                    isLaytimeStatement={false}
                    onDownloadElectricPdf={onDownloadElectricPdf}
                />
            </div>

            {state?.vesselData?.vesselName && (
                <div className="text-center mb-2">
                    <h4 className="font-medium text-[13px] uppercase text-[#313C4C] m-0 underline underline-offset-2 decoration-slate-600">
                        Statement of Facts for{" "}
                        <span className="text-slate-900 font-semibold">{state.vesselData.vesselName}</span>{" "}
                        B/L Dated{" "}
                        <span className="text-slate-900 font-semibold">
                            {state.vesselData.billOfLadingDate && moment(state.vesselData.billOfLadingDate).isValid()
                                ? moment(state.vesselData.billOfLadingDate).format("DD-MMM-YYYY")
                                : "N/A"}
                        </span>
                        : {state.vesselData.shipmentId || "N/A"} For{" "}
                        <span className="text-slate-900 font-semibold">{state.vesselData.port || "N/A"}</span> -{" "}
                        {state.vesselData.operation || "N/A"}
                    </h4>
                </div>
            )}

            {/* Vessel Details & User Defined Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                {/* Vessel Details */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 text-center">
                        Vessel Details
                    </div>
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <tbody>
                            {vesselFields?.map(({ key, label }) => {
                                if (key === "billOfLadingDate") {
                                    const rawVal = state?.vesselData?.billOfLadingDate;
                                    const validVal = rawVal && moment(rawVal).isValid() ? moment(rawVal) : null;
                                    return (
                                        <tr key={key} className="hover:bg-[#ffffcc]/60">
                                            <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                                                {label}
                                            </td>
                                            <td className="w-1/2 border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                                <MuiPickersUtilsProvider utils={MomentUtils}>
                                                    <Tooltip
                                                        title={
                                                            <span style={{ fontSize: "0.73rem" }}>
                                                                {validVal
                                                                    ? `Selected: ${validVal.format("DD/MM/YYYY HH:mm")}`
                                                                    : "Select date and time"}
                                                            </span>
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <KeyboardDateTimePicker
                                                            size="small"
                                                            variant="dialog"
                                                            ampm={false}
                                                            margin="none"
                                                            style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                                                            className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!justify-start [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!w-auto [&_.MuiInputBase-input]:!flex-initial [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-normal [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-1 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px] [&_.MuiInputBase-root.Mui-focused]:ring-1 [&_.MuiInputBase-root.Mui-focused]:ring-inset [&_.MuiInputBase-root.Mui-focused]:ring-amber-600 [&_.MuiInputBase-root.Mui-focused]:bg-white"
                                                            format="DD/MM/YYYY HH:mm"
                                                            value={validVal}
                                                            onChange={(date) => handleVesselChange(key, date)}
                                                            placeholder="DD/MM/YYYY HH:mm"
                                                            inputProps={{
                                                                style: {
                                                                    backgroundColor: "transparent",
                                                                    height: "19px",
                                                                    minHeight: "19px",
                                                                    maxHeight: "19px",
                                                                    padding: "0 4px",
                                                                    fontSize: "11px",
                                                                    lineHeight: "normal",
                                                                },
                                                            }}
                                                            InputProps={{
                                                                readOnly: true,
                                                                disableUnderline: true,
                                                                style: {
                                                                    fontSize: "11px",
                                                                    padding: "0 2px",
                                                                    height: "19px",
                                                                    minHeight: "19px",
                                                                    maxHeight: "19px",
                                                                    backgroundColor: "transparent",
                                                                },
                                                            }}
                                                            error={false}
                                                            helperText={null}
                                                            FormHelperTextProps={{ style: { display: "none" } }}
                                                            onError={() => { }}
                                                            keyboardIcon={<CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />}
                                                            KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                                                        />
                                                    </Tooltip>
                                                </MuiPickersUtilsProvider>
                                            </td>
                                        </tr>
                                    );
                                }
                                return (
                                    <tr key={key} className="hover:bg-[#ffffcc]/60">
                                        <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                                            {label}
                                        </td>
                                        <td className="w-1/2 border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                            <input
                                                type={key === "cargoQty" ? "number" : "text"}
                                                value={state?.vesselData?.[key] || ""}
                                                onChange={(e) => handleVesselChange(key, e.target.value)}
                                                className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* User Defined Fields */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs flex flex-col">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 text-center">
                        User Defined Fields
                    </div>
                    <div className="overflow-y-auto max-h-[220px] scroll-thin w-full flex-1">
                        <table className="w-full text-left border-collapse table-fixed">
                            <thead>
                                <tr className="bg-[#f0f2f5]">
                                    <th className="w-[32px] px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Add</th>
                                    <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Field Name</th>
                                    <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Value</th>
                                    <th className="w-[32px] px-1 py-0.5 text-center border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Del</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state?.userFields?.map((field, index) => (
                                    <tr key={index} className="hover:bg-[#ffffcc]/60 h-[19px]">
                                        <td className="w-[32px] border-r border-b border-slate-300 text-center align-middle h-[19px] p-0">
                                            <button
                                                onClick={() => addUserField(index)}
                                                className="w-full h-[19px] flex items-center justify-center text-emerald-700 hover:text-emerald-900 cursor-pointer"
                                                title="Add field below"
                                            >
                                                <BiPlus className="laytime-add-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                            </button>
                                        </td>
                                        <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                            <input
                                                type="text"
                                                value={field.label}
                                                onChange={(e) => handleUserFieldChange(index, "label", e.target.value)}
                                                className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium"
                                                placeholder="Field name"
                                            />
                                        </td>
                                        <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                            <input
                                                type="number"
                                                value={field.value}
                                                onChange={(e) => handleUserFieldChange(index, "value", e.target.value)}
                                                className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium"
                                                placeholder="Value"
                                            />
                                        </td>
                                        <td className="w-[32px] border-b border-slate-300 text-center align-middle h-[19px] p-0">
                                            <button
                                                onClick={() => removeUserField(index)}
                                                className="w-full h-[19px] flex items-center justify-center text-rose-600 hover:text-rose-800 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Remove field"
                                                disabled={state?.userFields?.length <= 1}
                                            >
                                                <FiTrash2 className="laytime-delete-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Events Table Section */}
            <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs mb-3">
                <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800">
                    Events Table
                </div>
                <div className="w-full overflow-hidden">
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <thead>
                            <tr>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[32px]">
                                    Add
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[45px]">
                                    Day
                                </th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[130px]">
                                    Time
                                </th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle">
                                    Event
                                </th>
                                <th className="px-1 py-0.5 text-center border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[32px]">
                                    Del
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {state?.events?.map((event, index) => (
                                <tr key={index} className="hover:bg-[#ffffcc]/60 h-[19px]">
                                    <td className="w-[32px] border-r border-b border-slate-300 text-center align-middle h-[19px] p-0">
                                        <button
                                            onClick={() => addRow(index)}
                                            className="w-full h-[19px] flex items-center justify-center text-emerald-700 hover:text-emerald-900 cursor-pointer"
                                            title="Add row below"
                                        >
                                            <BiPlus className="laytime-add-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                        </button>
                                    </td>
                                    <td className="w-[45px] border-r border-b border-slate-300 text-center align-middle bg-slate-50 h-[19px] px-1 text-[11px] font-medium text-slate-800 truncate">
                                        {event.day || "-"}
                                    </td>
                                    <td className="w-[130px] border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                        <MuiPickersUtilsProvider utils={MomentUtils}>
                                            <Tooltip
                                                title={
                                                    <span style={{ fontSize: "0.73rem" }}>
                                                        {event.time && moment(event.time).isValid()
                                                            ? `Selected: ${moment(event.time).format("DD/MM/YYYY HH:mm")}`
                                                            : "Select date and time"}
                                                    </span>
                                                }
                                                placement="top"
                                                arrow
                                            >
                                                <KeyboardDateTimePicker
                                                    size="small"
                                                    variant="dialog"
                                                    ampm={false}
                                                    margin="none"
                                                    style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                                                    className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!justify-start [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!w-auto [&_.MuiInputBase-input]:!flex-initial [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-normal [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-1 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px] [&_.MuiInputBase-root.Mui-focused]:ring-1 [&_.MuiInputBase-root.Mui-focused]:ring-inset [&_.MuiInputBase-root.Mui-focused]:ring-amber-600 [&_.MuiInputBase-root.Mui-focused]:bg-white"
                                                    format="DD/MM/YYYY HH:mm"
                                                    value={event.time && moment(event.time).isValid() ? moment(event.time) : null}
                                                    onChange={(date) => handleEventChange(index, "time", date)}
                                                    placeholder="DD/MM/YYYY HH:mm"
                                                    inputProps={{
                                                        style: {
                                                            backgroundColor: "transparent",
                                                            height: "19px",
                                                            minHeight: "19px",
                                                            maxHeight: "19px",
                                                            padding: "0 4px",
                                                            fontSize: "11px",
                                                            lineHeight: "normal",
                                                        },
                                                    }}
                                                    InputProps={{
                                                        readOnly: true,
                                                        disableUnderline: true,
                                                        style: {
                                                            fontSize: "11px",
                                                            padding: "0 2px",
                                                            height: "19px",
                                                            minHeight: "19px",
                                                            maxHeight: "19px",
                                                            backgroundColor: "transparent",
                                                        },
                                                    }}
                                                    error={false}
                                                    helperText={null}
                                                    FormHelperTextProps={{ style: { display: "none" } }}
                                                    onError={() => { }}
                                                    keyboardIcon={<CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />}
                                                    KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                                                />
                                            </Tooltip>
                                        </MuiPickersUtilsProvider>
                                    </td>
                                    <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                        <input
                                            type="text"
                                            className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-slate-400"
                                            value={event.event || ""}
                                            onChange={(e) => handleEventChange(index, "event", e.target.value)}
                                            placeholder="Enter event details..."
                                        />
                                    </td>
                                    <td className="w-[32px] border-b border-slate-300 text-center align-middle h-[19px] p-0">
                                        <button
                                            onClick={() => removeRow(index)}
                                            className="w-full h-[19px] flex items-center justify-center text-rose-600 hover:text-rose-800 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Remove row"
                                            disabled={state?.events?.length <= 1}
                                        >
                                            <FiTrash2 className="laytime-delete-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Remarks Section */}
            <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs mb-3">
                <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800">
                    Remarks
                </div>
                <div className="p-1.5 space-y-2">
                    {Object.entries(state?.remarks || {})
                        .filter(([type]) => type !== '_id')
                        ?.map(([type, lines]) => {
                            const titleMap = {
                                general: "General Remarks",
                                shippers: "Shippers Remarks",
                                shipOwners: "Ship Owners Remarks",
                            };
                            const title = titleMap[type] || `${type} Remarks`;
                            return (
                                <div key={type} className="border border-slate-300 bg-white overflow-hidden">
                                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800">
                                        {title}
                                    </div>
                                    <div className="divide-y divide-slate-300">
                                        {lines?.length > 0 && lines?.map((text, index) => (
                                            <div key={index} className="flex items-center h-[19px] hover:bg-[#ffffcc]/60 bg-[#ffffd6]">
                                                <div className="w-[32px] h-[19px] border-r border-slate-300 flex items-center justify-center bg-slate-50">
                                                    <button
                                                        onClick={() => addRemark(type, index)}
                                                        className="w-full h-[19px] flex items-center justify-center text-emerald-700 hover:text-emerald-900 cursor-pointer"
                                                        title="Add remark below"
                                                    >
                                                        <BiPlus className="laytime-add-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                                    </button>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="flex-1 h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-slate-400"
                                                    value={text}
                                                    onChange={(e) => handleRemarkChange(type, index, e.target.value)}
                                                    placeholder={`Enter ${type} remark...`}
                                                />
                                                <div className="w-[32px] h-[19px] border-l border-slate-300 flex items-center justify-center bg-slate-50">
                                                    <button
                                                        onClick={() => removeRemark(type, index)}
                                                        className="w-full h-[19px] flex items-center justify-center text-rose-600 hover:text-rose-800 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                                        disabled={lines.length <= 1}
                                                        title="Remove remark"
                                                    >
                                                        <FiTrash2 className="laytime-delete-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            {/* Statement of Facts Authors Section */}
            <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs mb-3">
                <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800">
                    Statement of Facts Authors
                </div>
                <div className="w-full overflow-hidden">
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <thead>
                            <tr className="bg-[#f0f2f5]">
                                <th className="w-[32px] px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Add</th>
                                <th className="w-[180px] px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Author Name</th>
                                <th className="w-[180px] px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Designation</th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Details</th>
                                <th className="w-[32px] px-1 py-0.5 text-center border-b border-slate-300 text-[11px] font-semibold text-slate-800 align-middle">Del</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state?.sof_authors?.map((person, index) => (
                                <tr key={person.id || index} className="hover:bg-[#ffffcc]/60 h-[19px]">
                                    <td className="w-[32px] border-r border-b border-slate-300 text-center align-middle h-[19px] p-0">
                                        <button
                                            onClick={handleAddPerson}
                                            className="w-full h-[19px] flex items-center justify-center text-emerald-700 hover:text-emerald-900 cursor-pointer"
                                            title="Add author below"
                                        >
                                            <BiPlus className="laytime-add-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                        </button>
                                    </td>
                                    <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                        <input
                                            type="text"
                                            value={person.name || ""}
                                            onChange={(e) => handlePersonnelChange(person.id, "name", e.target.value)}
                                            className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-slate-400"
                                            placeholder="Enter name"
                                        />
                                    </td>
                                    <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                        <input
                                            type="text"
                                            value={person.designation || ""}
                                            onChange={(e) => handlePersonnelChange(person.id, "designation", e.target.value)}
                                            className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-slate-400"
                                            placeholder="Enter designation"
                                        />
                                    </td>
                                    <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                                        <input
                                            type="text"
                                            value={person.details || ""}
                                            onChange={(e) => handlePersonnelChange(person.id, "details", e.target.value)}
                                            className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-slate-400"
                                            placeholder="Enter details"
                                        />
                                    </td>
                                    <td className="w-[32px] border-b border-slate-300 text-center align-middle h-[19px] p-0">
                                        <button
                                            onClick={() => handleDeletePerson(person.id)}
                                            className="w-full h-[19px] flex items-center justify-center text-rose-600 hover:text-rose-800 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Delete author"
                                            disabled={state?.sof_authors?.length <= 1}
                                        >
                                            <FiTrash2 className="laytime-delete-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <HtmlImportGuide open={openUploadHTML} onClose={() => setOpenUploadHTML(false)} onChooseFile={() => inputFileRef.current?.click()} kind="facts" />
        </div>
    );
};

export default StatementofFactsModel;
